import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import { useEffect } from 'react';

const Privacy = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen">
      <SEO 
        title="Privacy Policy - EZ Cloud Data Protection & User Rights"
        description="Learn how EZ Cloud protects your data, manages cookies, and respects user privacy. Comprehensive privacy policy covering data collection and user rights."
        canonical="https://ezcloud.ai/privacy"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Privacy Policy', url: 'https://ezcloud.ai/privacy' },
        ]}
      />
      <Navigation />
      <Breadcrumbs />
      <div className="container mx-auto px-4 py-16 max-w-4xl">
        <h1 className="text-4xl font-bold text-white mb-8">Privacy Policy</h1>
        <div className="max-w-none space-y-6 text-gray-300 text-lg leading-relaxed">
          <p className="text-sm text-gray-400">Last updated: {new Date().toLocaleDateString()}</p>
          
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Introduction</h2>
            <p>
              EZ Cloud ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your information when you visit our website.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Information We Collect</h2>
            <h3 className="text-xl font-medium text-white mb-2">Analytics Data</h3>
            <p>We use Google Analytics to understand how visitors interact with our website. This service automatically collects:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>IP addresses (anonymized)</li>
              <li>Browser type and version</li>
              <li>Pages visited and time spent on pages</li>
              <li>Referring websites</li>
              <li>Device information (screen size, operating system)</li>
              <li>Geographic location (country/city level)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">How We Use Information</h2>
            <p>We use the collected information to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Improve our website performance and user experience</li>
              <li>Understand which content is most valuable to visitors</li>
              <li>Analyze traffic patterns and optimize our services</li>
              <li>Generate aggregate statistics about website usage</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Cookies</h2>
            <p>
              Our website uses cookies to enable Google Analytics. You can control cookie preferences through your browser settings or our cookie consent banner. Essential cookies are necessary for basic website functionality.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Third-Party Services</h2>
            <h3 className="text-xl font-medium text-white mb-2">Google Analytics</h3>
            <p>
              We use Google Analytics to analyze website traffic. Google Analytics uses cookies to track user interactions. You can opt-out of Google Analytics by visiting the Google Analytics opt-out page or adjusting your cookie preferences.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Your Rights (EU Visitors)</h2>
            <p>If you are located in the European Union, you have the right to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Access your personal data</li>
              <li>Rectify inaccurate personal data</li>
              <li>Erase your personal data</li>
              <li>Restrict processing of your personal data</li>
              <li>Data portability</li>
              <li>Object to processing</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Data Retention</h2>
            <p>
              Google Analytics data is automatically deleted after 26 months. We do not store personal data longer than necessary for the purposes outlined in this policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Contact Information</h2>
            <p>
              If you have questions about this Privacy Policy or wish to exercise your rights, please contact us at:
            </p>
            <p><strong className="text-white">Email:</strong> hello@ezcloud.ai</p>
            <p><strong className="text-white">Address:</strong> 1134 S Black Horse Pike Suite 266, Blackwood, NJ 08012</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Updates to Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Privacy;